import React from 'react';
import PropTypes from 'prop-types';
import { Button } from './ui/button';
import { Instagram, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNavThemeOnBackground } from '../hooks/useNavThemeOnBackground';
import { useNavigationActions } from '../hooks/useNavigationActions';
import { useOptimizedScroll } from '../hooks/useOptimizedScroll';
import { NAVIGATION_CONSTANTS } from '../constants/navigation';

const Navigation = ({ 
  experimentalBlendMode = false, 
  theme = 'dark',
  isLegalPage = false 
}) => {
  const { isScrolled } = useOptimizedScroll({ 
    throttleDelay: NAVIGATION_CONSTANTS.SCROLL_THROTTLE_DELAY,
    useThrottle: true 
  });
  const { scrollToSection, navigateToSection, handleLogoClick } = useNavigationActions();

  // Enable theme detection based on background
  useNavThemeOnBackground('main-navigation', {
    fallbackTheme: theme,
    probeOffset: NAVIGATION_CONSTANTS.THEME_PROBE_OFFSET,
    debounceDelay: NAVIGATION_CONSTANTS.THEME_DEBOUNCE_DELAY
  });


  return (
    <nav 
      id="main-navigation"
      className={`glass-nav hidden md:block fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
        isScrolled 
          ? 'pt-6' 
          : 'bg-transparent pt-12'
      }`}
      data-theme={theme}
    >
      <div 
        className={`container mx-auto relative ${
          isScrolled 
            ? 'glass-panel py-6 md:max-w-2xl max-w-sm mx-auto rounded-full md:px-6 px-3 mx-3'
            : 'py-8 max-w-7xl md:px-6 px-3 mx-3'
        }`}
        style={{
          transition: `all ${NAVIGATION_CONSTANTS.GLASS_TRANSITION_DURATION}ms ${NAVIGATION_CONSTANTS.GLASS_CUBIC_BEZIER}`
        }}
      >
        <div 
          className={`flex items-center w-full relative transition-all ${
            isScrolled ? 'justify-center' : 'justify-between'
          }`}
          style={{
            paddingTop: '12px',
            paddingBottom: '12px',
            transition: `all ${NAVIGATION_CONSTANTS.GLASS_TRANSITION_DURATION}ms ${NAVIGATION_CONSTANTS.GLASS_CUBIC_BEZIER}`
          }}
        >
          {/* Logo */}
          <div
            className={`transition-all ${experimentalBlendMode ? 'blend-difference' : ''}`}
            style={{
              transition: `transform ${NAVIGATION_CONSTANTS.GLASS_TRANSITION_DURATION}ms ${NAVIGATION_CONSTANTS.GLASS_CUBIC_BEZIER}`,
              transform: isScrolled ? 'translateX(0)' : 'translateX(0)'
            }}
          >
            {isLegalPage ? (
              <Link to="/" aria-label="Return to Antix homepage">
                <h1 className="nav-text text-2xl font-semibold cursor-pointer font-['Space_Grotesk']">
                  Antix
                </h1>
              </Link>
            ) : (
              <h1
                className="nav-text text-2xl font-semibold cursor-pointer font-['Space_Grotesk']"
                onClick={() => {
                  // Track home/logo click
                  if (window.gtag) {
                    window.gtag('event', 'navigation_click', {
                      event_category: 'Navigation',
                      event_label: 'home',
                    });
                  }
                  handleLogoClick();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // Track home/logo click
                    if (window.gtag) {
                      window.gtag('event', 'navigation_click', {
                        event_category: 'Navigation',
                        event_label: 'home',
                      });
                    }
                    handleLogoClick();
                  }
                }}
                aria-label="Scroll to top of page"
              >
                Antix
              </h1>
            )}
          </div>

          {/* Vertical Pipe Separator - Only visible when scrolled on desktop */}
          <div 
            className={`hidden md:block transition-all ${
              isScrolled ? 'opacity-100 scale-100 mx-6' : 'opacity-0 scale-0 mx-0'
            }`}
            style={{
              transition: 'all 900ms cubic-bezier(0.25, 0.1, 0.25, 1)',
              width: isScrolled ? '1px' : '0px'
            }}
          >
            <div 
              className="nav-separator w-px"
              style={{ height: '28px' }}
            />
          </div>

          {/* Desktop Navigation Links - Hidden on mobile */}
          <div 
            className={`hidden md:flex items-center ${
              isScrolled ? 'space-x-6' : 'md:absolute md:left-1/2 md:transform md:-translate-x-1/2 space-x-8'
            } ${experimentalBlendMode ? 'blend-difference' : ''}`}
            style={{
              transition: 'all 900ms cubic-bezier(0.25, 0.1, 0.25, 1)'
            }}
          >
            <button
              onClick={() => {
                // Track navigation click
                if (window.gtag) {
                  window.gtag('event', 'navigation_click', {
                    event_category: 'Navigation',
                    event_label: 'about',
                  });
                }
                isLegalPage ? navigateToSection('about') : scrollToSection('about');
              }}
              className="nav-text nav-link transition-colors duration-200 font-['Inter'] font-medium text-base"
              aria-label={isLegalPage ? "Navigate to About section on homepage" : "Scroll to About section"}
            >
              About
            </button>
            <button
              onClick={() => {
                // Track navigation click
                if (window.gtag) {
                  window.gtag('event', 'navigation_click', {
                    event_category: 'Navigation',
                    event_label: 'features',
                  });
                }
                isLegalPage ? navigateToSection('features') : scrollToSection('features');
              }}
              className="nav-text nav-link transition-colors duration-200 font-['Inter'] font-medium text-base"
              aria-label={isLegalPage ? "Navigate to Features section on homepage" : "Scroll to Features section"}
            >
              Features
            </button>
            <button
              onClick={() => {
                // Track navigation click
                if (window.gtag) {
                  window.gtag('event', 'navigation_click', {
                    event_category: 'Navigation',
                    event_label: 'mission',
                  });
                }
                isLegalPage ? navigateToSection('mission') : scrollToSection('mission');
              }}
              className="nav-text nav-link transition-colors duration-200 font-['Inter'] font-medium text-base"
              aria-label={isLegalPage ? "Navigate to Mission section on homepage" : "Scroll to Mission section"}
            >
              Mission
            </button>
          </div>

          {/* Vertical Pipe Separator between Nav and Social Icons - Only visible when scrolled on desktop */}
          <div 
            className={`hidden md:block transition-all ${
              isScrolled ? 'opacity-100 scale-100 mx-6' : 'opacity-0 scale-0 mx-0'
            }`}
            style={{
              transition: 'all 900ms cubic-bezier(0.25, 0.1, 0.25, 1)',
              width: isScrolled ? '1px' : '0px'
            }}
          >
            <div 
              className="nav-separator w-px"
              style={{ height: '28px' }}
            />
          </div>

          {/* Desktop Social Media Icons - Hidden on mobile */}
          <div 
            className={`hidden md:flex items-center space-x-2 ${isScrolled ? '' : ''}`}
            style={{
              transition: 'transform 900ms cubic-bezier(0.25, 0.1, 0.25, 1)',
              transform: isScrolled ? 'translateX(0)' : 'translateX(0)'
            }}
          >
            {/* Instagram */}
            <a
              href="https://www.instagram.com/antix.app"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-social-icon p-2 rounded-full transition-all duration-200"
              aria-label="Instagram"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'social_click', {
                    event_category: 'Social',
                    event_label: 'instagram_nav',
                  });
                }
              }}
            >
              <Instagram size={20} className="nav-icon" />
            </a>

            {/* TikTok */}
            <a
              href="https://www.tiktok.com/@antix.the.app"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-social-icon p-2 rounded-full transition-all duration-200"
              aria-label="TikTok"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'social_click', {
                    event_category: 'Social',
                    event_label: 'tiktok_nav',
                  });
                }
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="nav-icon"
              >
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
            </a>

            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/company/antixapp"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-social-icon p-2 rounded-full transition-all duration-200"
              aria-label="LinkedIn"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'social_click', {
                    event_category: 'Social',
                    event_label: 'linkedin_nav',
                  });
                }
              }}
            >
              <Linkedin size={20} className="nav-icon" />
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

Navigation.propTypes = {
  experimentalBlendMode: PropTypes.bool,
  theme: PropTypes.oneOf(['dark', 'light']),
  isLegalPage: PropTypes.bool
};

Navigation.defaultProps = {
  experimentalBlendMode: false,
  theme: 'dark',
  isLegalPage: false
};

export default Navigation;
